import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Bell, AlertTriangle, Clock, CheckCircle, XCircle, 
  AlertCircle, TrendingUp, Package, Users, RefreshCw 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

const NotificationsCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, critical, warning, info

  useEffect(() => {
    fetchNotifications();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch various data to generate notifications
      const [sosDevices, agingDevices, rfr, inventory] = await Promise.all([
        axios.get(`${API_URL}/api/trc/devices/sos/list`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/trc/inventory/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/trc/devices/rfr/list`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/trc/inventory/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const generatedNotifications = [];

      // SOS Devices
      if (sosDevices.data.devices?.length > 0) {
        generatedNotifications.push({
          id: 'sos-1',
          type: 'critical',
          title: 'SOS Priority Devices',
          message: `${sosDevices.data.devices.length} devices marked as SOS priority need immediate attention`,
          timestamp: new Date().toISOString(),
          icon: AlertTriangle,
          color: 'red',
          action: '/trc/inventory?filter=sos',
          actionText: 'View SOS Devices'
        });
      }

      // Aging devices (30+ days)
      const aging30Plus = agingDevices.data.aging?.['30+_days'] || 0;
      if (aging30Plus > 0) {
        generatedNotifications.push({
          id: 'aging-1',
          type: 'warning',
          title: 'Devices Aging Beyond 30 Days',
          message: `${aging30Plus} devices have been in TRC for more than 30 days`,
          timestamp: new Date().toISOString(),
          icon: Clock,
          color: 'orange',
          action: '/trc/inventory',
          actionText: 'View Aging Devices'
        });
      }

      // Ready for Repair backlog
      if (rfr.data.devices?.length > 10) {
        generatedNotifications.push({
          id: 'rfr-1',
          type: 'warning',
          title: 'Large RFR Backlog',
          message: `${rfr.data.devices.length} devices are ready for repair. Consider assigning more engineers.`,
          timestamp: new Date().toISOString(),
          icon: Package,
          color: 'yellow',
          action: '/trc/admin',
          actionText: 'Assign Devices'
        });
      }

      // Low engineer capacity
      const totalDevices = inventory.data.total_devices || 0;
      if (totalDevices > 50) {
        generatedNotifications.push({
          id: 'capacity-1',
          type: 'info',
          title: 'High Device Volume',
          message: `Total TRC inventory: ${totalDevices} devices. Monitor engineer capacity.`,
          timestamp: new Date().toISOString(),
          icon: TrendingUp,
          color: 'blue',
          action: '/trc/analytics',
          actionText: 'View Analytics'
        });
      }

      // FQC Pass Rate
      const fqcPass = inventory.data.status_wise?.fqc_pass || 0;
      const fqcFail = inventory.data.status_wise?.fqc_fail_rtr || 0;
      const passRate = fqcPass + fqcFail > 0 ? (fqcPass / (fqcPass + fqcFail)) * 100 : 100;
      
      if (passRate < 70 && fqcPass + fqcFail > 5) {
        generatedNotifications.push({
          id: 'fqc-1',
          type: 'critical',
          title: 'Low FQC Pass Rate',
          message: `FQC pass rate is ${passRate.toFixed(1)}%. Review repair quality.`,
          timestamp: new Date().toISOString(),
          icon: XCircle,
          color: 'red',
          action: '/trc/analytics',
          actionText: 'View Performance'
        });
      } else if (passRate >= 90 && fqcPass > 10) {
        generatedNotifications.push({
          id: 'fqc-2',
          type: 'success',
          title: 'Excellent FQC Performance',
          message: `FQC pass rate is ${passRate.toFixed(1)}%. Great work!`,
          timestamp: new Date().toISOString(),
          icon: CheckCircle,
          color: 'green',
          action: '/trc/analytics',
          actionText: 'View Analytics'
        });
      }

      setNotifications(generatedNotifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredNotifications = notifications.filter(notif => 
    filter === 'all' || notif.type === filter
  );

  const getTypeColor = (type) => {
    const colors = {
      'critical': 'bg-red-100 text-red-800 border-red-200',
      'warning': 'bg-orange-100 text-orange-800 border-orange-200',
      'info': 'bg-blue-100 text-blue-800 border-blue-200',
      'success': 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[type] || colors.info;
  };

  const getIconColor = (color) => {
    const colors = {
      'red': 'text-red-600',
      'orange': 'text-orange-600',
      'yellow': 'text-yellow-600',
      'blue': 'text-blue-600',
      'green': 'text-green-600'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center" style={{ color: ZOBOX_BLUE }}>
            <Bell className="w-8 h-8 mr-3" />
            Notifications Center
          </h1>
          <p className="text-gray-600 mt-1">
            Real-time alerts and system notifications
          </p>
        </div>
        <button
          onClick={fetchNotifications}
          className="flex items-center px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors"
          style={{ backgroundColor: ZOBOX_BLUE }}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex space-x-2">
          {[
            { key: 'all', label: 'All', count: notifications.length },
            { key: 'critical', label: 'Critical', count: notifications.filter(n => n.type === 'critical').length },
            { key: 'warning', label: 'Warning', count: notifications.filter(n => n.type === 'warning').length },
            { key: 'info', label: 'Info', count: notifications.filter(n => n.type === 'info').length },
            { key: 'success', label: 'Success', count: notifications.filter(n => n.type === 'success').length }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === tab.key
                  ? 'text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              style={filter === tab.key ? { backgroundColor: ZOBOX_BLUE } : {}}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-700 font-medium">Critical</p>
              <p className="text-2xl font-bold text-red-800">
                {notifications.filter(n => n.type === 'critical').length}
              </p>
            </div>
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
        </div>

        <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-700 font-medium">Warnings</p>
              <p className="text-2xl font-bold text-orange-800">
                {notifications.filter(n => n.type === 'warning').length}
              </p>
            </div>
            <AlertCircle className="w-8 h-8 text-orange-600" />
          </div>
        </div>

        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-700 font-medium">Info</p>
              <p className="text-2xl font-bold text-blue-800">
                {notifications.filter(n => n.type === 'info').length}
              </p>
            </div>
            <AlertCircle className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-700 font-medium">Success</p>
              <p className="text-2xl font-bold text-green-800">
                {notifications.filter(n => n.type === 'success').length}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: ZOBOX_BLUE }}></div>
            <p className="text-gray-600 mt-4">Loading notifications...</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <Bell className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-600 text-lg">No notifications at this time</p>
            <p className="text-gray-500 text-sm mt-2">All systems running smoothly!</p>
          </div>
        ) : (
          filteredNotifications.map(notification => {
            const Icon = notification.icon;
            return (
              <div
                key={notification.id}
                className={`bg-white rounded-lg shadow-md border-l-4 p-6 hover:shadow-lg transition-shadow ${
                  notification.type === 'critical' ? 'border-red-500' :
                  notification.type === 'warning' ? 'border-orange-500' :
                  notification.type === 'success' ? 'border-green-500' :
                  'border-blue-500'
                }`}
              >
                <div className="flex items-start">
                  <div className={`p-3 rounded-full ${
                    notification.type === 'critical' ? 'bg-red-100' :
                    notification.type === 'warning' ? 'bg-orange-100' :
                    notification.type === 'success' ? 'bg-green-100' :
                    'bg-blue-100'
                  }`}>
                    <Icon className={`w-6 h-6 ${getIconColor(notification.color)}`} />
                  </div>
                  
                  <div className="flex-1 ml-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">
                          {notification.title}
                        </h3>
                        <p className="text-gray-600 mt-1">
                          {notification.message}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getTypeColor(notification.type)}`}>
                        {notification.type.toUpperCase()}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-sm text-gray-500">
                        {new Date(notification.timestamp).toLocaleString()}
                      </span>
                      {notification.action && (
                        <button
                          onClick={() => window.location.href = notification.action}
                          className="text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-colors"
                          style={{ backgroundColor: ZOBOX_BLUE }}
                        >
                          {notification.actionText}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default NotificationsCenter;
